/**
 * Common theme system for the unified application
 *
 * Provides consistent Fluent UI theme configuration, colors, typography,
 * and spacing across all route modules.
 *
 * Validates: Requirements 4.1, 4.3
 */

import { createTheme, ITheme } from '@fluentui/react';

/**
 * Application color palette
 * Based on CAA branding and accessibility guidelines
 */
export const AppColors = {
  // Primary colors (CAA branding)
  primary: '#0078d4',
  primaryDark: '#106ebe',
  primaryLight: '#40e0ff',

  // Secondary colors
  secondary: '#6c757d',
  secondaryDark: '#495057',
  secondaryLight: '#adb5bd',

  // Background colors
  background: '#ffffff',
  backgroundSecondary: '#faf9f8',
  backgroundTertiary: '#f3f2f1',

  // Surface colors
  surface: '#ffffff',
  surfaceElevated: '#ffffff',

  // Text colors
  text: '#323130',
  textSecondary: '#605e5c',
  textTertiary: '#a19f9d',
  textOnPrimary: '#ffffff',

  // Status colors
  success: '#107c10',
  warning: '#ff8c00',
  error: '#d13438',
  info: '#0078d4',

  // Border colors
  border: '#edebe9',
  borderStrong: '#c8c6c4',

  // Interactive colors
  hover: '#f3f2f1',
  pressed: '#edebe9',
  selected: '#deecf9',
} as const;

/**
 * Typography scale
 * Consistent font sizes and weights across the application
 */
export const Typography = {
  fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
  fontSize: {
    xs: '10px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px',
    xxxl: '24px',
    mega: '32px',
  },
  fontWeight: {
    regular: 400,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.4,
    relaxed: 1.6,
  },
} as const;

/**
 * Spacing scale
 * Consistent spacing values for margins, padding, and gaps
 */
export const Spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
  mega: '48px',
} as const;

/**
 * Elevation (shadow) system
 * Consistent shadow values for depth and hierarchy
 */
export const Elevation = {
  none: 'none',
  subtle: '0 1px 2px rgba(0, 0, 0, 0.1)',
  low: '0 2px 4px rgba(0, 0, 0, 0.1)',
  medium: '0 4px 8px rgba(0, 0, 0, 0.12)',
  high: '0 8px 16px rgba(0, 0, 0, 0.14)',
  highest: '0 16px 32px rgba(0, 0, 0, 0.16)',
} as const;

/**
 * Border radius values
 * Consistent corner radius for components
 */
export const BorderRadius = {
  none: '0px',
  sm: '2px',
  md: '4px',
  lg: '8px',
  xl: '12px',
  round: '50%',
} as const;

/**
 * Fluent UI theme configuration
 * Creates a complete theme object for Fluent UI components
 */
export const AppTheme: ITheme = createTheme({
  palette: {
    themePrimary: AppColors.primary,
    themeLighterAlt: '#eff6fc',
    themeLighter: '#deecf9',
    themeLight: '#c7e0f4',
    themeTertiary: '#71afe5',
    themeSecondary: '#2b88d8',
    themeDarkAlt: '#106ebe',
    themeDark: '#005a9e',
    themeDarker: '#004578',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
  },
  fonts: {
    small: {
      fontSize: Typography.fontSize.sm,
      fontWeight: Typography.fontWeight.regular,
      fontFamily: Typography.fontFamily,
    },
    medium: {
      fontSize: Typography.fontSize.md,
      fontWeight: Typography.fontWeight.regular,
      fontFamily: Typography.fontFamily,
    },
    mediumPlus: {
      fontSize: Typography.fontSize.lg,
      fontWeight: Typography.fontWeight.regular,
      fontFamily: Typography.fontFamily,
    },
    large: {
      fontSize: Typography.fontSize.xl,
      fontWeight: Typography.fontWeight.regular,
      fontFamily: Typography.fontFamily,
    },
    xLarge: {
      fontSize: Typography.fontSize.xxl,
      fontWeight: Typography.fontWeight.semibold,
      fontFamily: Typography.fontFamily,
    },
    xxLarge: {
      fontSize: Typography.fontSize.xxxl,
      fontWeight: Typography.fontWeight.semibold,
      fontFamily: Typography.fontFamily,
    },
    mega: {
      fontSize: Typography.fontSize.mega,
      fontWeight: Typography.fontWeight.bold,
      fontFamily: Typography.fontFamily,
    },
  },
  semanticColors: {
    bodyBackground: AppColors.background,
    bodyText: AppColors.text,
    bodyTextChecked: AppColors.text,
    bodySubtext: AppColors.textSecondary,
    bodyFrameBackground: AppColors.backgroundSecondary,
    bodyFrameDivider: AppColors.border,
    bodyStandoutBackground: AppColors.backgroundTertiary,
    defaultStateBackground: AppColors.surface,
    actionLink: AppColors.primary,
    actionLinkHovered: AppColors.primaryDark,
    link: AppColors.primary,
    linkHovered: AppColors.primaryDark,
    buttonBackground: AppColors.surface,
    buttonBackgroundChecked: AppColors.selected,
    buttonBackgroundHovered: AppColors.hover,
    buttonBackgroundPressed: AppColors.pressed,
    buttonBorder: AppColors.border,
    primaryButtonBackground: AppColors.primary,
    primaryButtonBackgroundHovered: AppColors.primaryDark,
    primaryButtonBackgroundPressed: AppColors.primaryDark,
    primaryButtonText: AppColors.textOnPrimary,
    inputBackground: AppColors.surface,
    inputBackgroundChecked: AppColors.selected,
    inputBorder: AppColors.border,
    inputBorderHovered: AppColors.borderStrong,
    inputForegroundChecked: AppColors.primary,
    inputPlaceholderText: AppColors.textTertiary,
    errorBackground: '#fdf2f2',
    errorText: AppColors.error,
    warningBackground: '#fff8f0',
    warningText: AppColors.warning,
    successBackground: '#f0f8f0',
    successText: AppColors.success,
  },
});

/**
 * Application theme interface
 * Provides a structured way to access theme values
 */
export interface AppThemeInterface {
  colors: typeof AppColors;
  typography: typeof Typography;
  spacing: typeof Spacing;
  elevation: typeof Elevation;
  borderRadius: typeof BorderRadius;
  fluentTheme: ITheme;
}

/**
 * Complete application theme object
 * Combines all theme tokens into a single exportable object
 */
export const completeAppTheme: AppThemeInterface = {
  colors: AppColors,
  typography: Typography,
  spacing: Spacing,
  elevation: Elevation,
  borderRadius: BorderRadius,
  fluentTheme: AppTheme,
};
